/**
 * PATH: packages/bills/src/components/_primitives.tsx
 * Extracted from BillPoPanel.tsx to keep files under the 1000-line
 * ceiling. Behaviour-preserving: these were module-private helpers in
 * BillPoPanel.tsx; only the (internal) import path changed.
 */
import type {
  BillPoVarianceRow,
  PurchaseOrder,
} from '../types';


export function SourceBadge({ source }: { source: PurchaseOrder['source'] }) {
  if (source === 'xero_sync') return null;
  const label = source === 'manual' ? 'manual' : 'AI';
  return (
    <span className="rounded bg-muted px-1 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
      {label}
    </span>
  );
}


export function StatusBadge({ status }: { status: PurchaseOrder['status'] }) {
  return (
    <span className="rounded border border-border bg-background px-1 py-0.5 text-[9px] uppercase tracking-wider text-muted-foreground">
      {status}
    </span>
  );
}


export function FlagBadge({ flag }: { flag: BillPoVarianceRow['flag'] }) {
  if (flag === 'ok') {
    return (
      <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200">
        ok
      </span>
    );
  }
  const label = flag === 'qty_mismatch' ? 'qty Δ' : 'price Δ';
  return (
    <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
      {label}
    </span>
  );
}


export function fmtDelta(value: string | null): string {
  if (value == null || value === '') return '—';
  const n = parseFloat(value);
  if (Number.isNaN(n)) return value;
  if (Math.abs(n) < 0.005) return '0';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(2)}`;
}


export function Section({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      {subtitle && (
        <p className="mb-1 text-[11px] text-muted-foreground">{subtitle}</p>
      )}
      <div className="space-y-1.5">{children}</div>
    </section>
  );
}


export function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-[11px]">
      <span className="mb-0.5 block text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}


export function Stat({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="rounded-md border border-border bg-background px-2 py-1.5">
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`tabular-nums ${valueClass || 'text-foreground'}`}>{value}</p>
    </div>
  );
}


export function Empty({ children }: { children: React.ReactNode }) {
  return (
    <aside className="flex h-full w-full items-center justify-center border-l border-border bg-card p-6 text-center text-sm text-muted-foreground">
      <div>{children}</div>
    </aside>
  );
}


export const inputCls =
  'w-full rounded border border-border bg-background px-2 py-1 text-xs ' +
  'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/40';


export const btnPrimary =
  'inline-flex items-center justify-center rounded-md bg-primary px-2.5 py-1 ' +
  'text-xs font-medium text-primary-foreground hover:bg-primary/90 ' +
  'disabled:opacity-50';


export const btnSecondary =
  'inline-flex items-center justify-center rounded-md border border-border bg-background ' +
  'px-2.5 py-1 text-xs font-medium hover:bg-accent disabled:opacity-50';


export const btnGhost =
  'inline-flex items-center justify-center rounded-md px-2 py-0.5 text-[11px] ' +
  'text-muted-foreground hover:bg-accent hover:text-foreground';
